

/**
 * SkillStudio — ADR-094 P1.4 (+ polish): the "skill-as-router" view of the
 * Workflow Studio. Left rail: agents → their skills (with a router badge = route
 * count) + a search filter. Selecting a skill shows it — a health badge (live
 * validation), its routes → workflows (both-direction nav), the raw SKILL.md,
 * and an Edit surface: the skill copilot (Chat), a raw editor with DEBOUNCED
 * auto-validation, a live routes preview parsed from the draft, and Propose
 * (fleet_owner). An unsaved-edits guard protects Cancel / switching skills.
 *
 * Wired to the ADR-094 P0/P1 endpoints (getSkill / validateSkill / skillCopilot
 * / proposeSkill). Self-contained: an alternate top-level view so the proven
 * activity-editing JSX in Workflows.tsx stays untouched.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useFleetVersion } from '../hooks/useFleetVersion'
import { isPrefixStaged, useStagedPaths } from '../hooks/useStagedPaths'
import { StagedBadge } from '../components/StagedBadge'
import { GateInconclusiveNote } from '../components/GateInconclusiveNote'
import {
  api,
  AuthorTestsFailingError,
  parseDeleteBlocked,
  parseSkillChangePending,
  type DeleteEdge,
  CopilotNotConfiguredError,
  UnauthorizedError,
  type ActivitySummary,
  type AgentSkills,
  type CopilotMessage,
  type FailingCheck,
  type ProposeSkillResult,
  type SkillChangePendingError,
  type SkillContent,
  type SkillCritiqueResponse,
  type SkillCritiqueSuggestion,
  type SkillRoute,
  type SkillValidation,
  type FleetFileNode,
} from '../api/client'
import { FailingChecksPanel } from '../components/FailingChecksPanel'
import { DeletePanel } from '../components/DeletePanel'
import { DataRefFieldAffordance } from './DataRefFieldAffordance'
import { ensureDataPinInSkillMd, parseRoutesFromRaw, setRoutesInSkillMd, lineDiff, type DiffLine } from './skillDraft'
import { slugifyWorkflowName } from './workflowDraft'
import { ConvertPanel } from './ConvertPanel'
import { SkillFiles } from './SkillFiles'
import { useAttentionNotify } from '../context/AttentionContext'
import { copilotJobAttentionId, localAttentionId } from '../utils/attentionStore'
import { inheritedCount, isInheritedFromPlatform, PLATFORM_SKILLS_PATH } from './platformSkills'

/**
 * ADR-123 D3/D5 — the references that would be left dangling by this deletion.
 *
 * This is the AUTHORITATIVE list. SkillFiles also warns while staging, but that
 * warning reads only this skill's own SKILL.md; the server scans the whole fleet,
 * so a line in another skill or in fleet config appears only here.
 */
function DeleteBlockedPanel({ blocked }: { blocked: DeleteEdge[] }) {
  return (
    <div data-testid="delete-blocked" className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
      <p className="font-medium text-amber-900">
        Not proposed — {blocked.length} reference{blocked.length === 1 ? '' : 's'} would be left pointing at a deleted file.
      </p>
      <ul className="mt-2 space-y-1">
        {blocked.map(b => (
          <li key={`${b.file}:${b.line}`} className="font-mono text-xs text-amber-900">
            {b.file}:{b.line}
            <span className="ml-2 font-sans text-amber-800">{b.detail}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-amber-800">
        Edit the lines inside this skill and propose again in the same change. A line in another
        skill or in fleet config has to be repaired in its own change first.
      </p>
    </div>
  )
}

// Minimal starting point for a brand-new SKILL.md. The user (or the copilot /
// routes editor) fills it in; the copilot is grounded on the agent's real
// workflows so it can wire routes: without the user hand-writing YAML. `name`
// MUST equal the slug (the validator enforces it), so it's derived, not typed.
function newSkillScaffold(slug: string): string {
  const title = slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  return `---
name: ${slug}
description: TODO — one line: what this skill does and when the agent should use it.
---

# ${title}

TODO — describe what the agent should do for this skill.

If this skill hands off to workflows, add routes below (or use the routes editor /
the "Wire/repair routes" copilot button) so each way an operator asks for the work
maps to the right workflow.
`
}

// Canned copilot prompt for the one-click "wire/repair routes" action — the
// copilot's grounding already lists the agent's real activities + triggers.
const WIRE_ROUTES_PROMPT =
  'Generate or repair this skill\'s `routes:` block. Map each way an operator would ask for this agent\'s work to the right workflow, using ONLY the agent\'s real activities from your grounding. Fix any dangling route (a workflow that does not exist), remove duplicate intents, and keep each intent consistent with the target activity\'s triggers. Return the full updated SKILL.md.'

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

export function SkillStudio({
  canPropose,
  onOpenWorkflow,
  initialAgent,
  scopedAgent,
  initialName,
}: {
  canPropose: boolean
  onOpenWorkflow: (agent: string, name: string, edit?: boolean) => void
  // The agent context carried in from the Workflows view, so switching to Skills
  // lands on the same agent instead of a blank/unfiltered list. Ignored once
  // `scopedAgent` is set (it always wins).
  initialAgent?: string
  // ADR-111 IA-1: when set, this is the Skills FACET of one agent (embedded by
  // FleetEditor.tsx) — the internal agent rail/filter dropdown are hidden and
  // the list is locked to this agent instead of browsing the whole fleet.
  scopedAgent?: string
  // The skill named in the URL (/fleet-editor/agents/:agent/skills/:name),
  // opened once the list has loaded — ADR-111 IA-6. Only meaningful alongside
  // scopedAgent, which is what makes the slug unambiguous.
  initialName?: string
}) {
  const navigate = useNavigate()
  const [agentSkills, setAgentSkills] = useState<AgentSkills[]>([])
  // ADR-111 D2: per-skill "staged — not yet live" badges (see the rail render
  // below) — the same staged-path set FilesStudio/Workflows use.
  const { paths: stagedPaths } = useStagedPaths()
  // The fleet tree, read ONCE here beside the staged paths rather than inside
  // SkillDetail: that component is remounted by key on every selection, so a
  // fetch of the whole fleet would re-run each time a skill is clicked. Null
  // until it arrives, which SkillFiles renders differently from "no files".
  const [treeFiles, setTreeFiles] = useState<FleetFileNode[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [selected, setSelected] = useState<{ agent: string; slug: string } | null>(null)
  // A brand-new skill being authored: right pane shows the scaffold editor.
  const [newSkill, setNewSkill] = useState<{ agent: string; slug: string; scaffold: string } | null>(null)
  const [newSkillOpen, setNewSkillOpen] = useState(false)
  // When the form is opened from a rail "＋", pre-select that agent.
  const [newSkillAgent, setNewSkillAgent] = useState<string | undefined>(undefined)
  const [roster, setRoster] = useState<string[]>([]) // full agent roster (incl. agents with zero skills)
  // ADR-103: core agents, excluded from every AUTHORING picker below (New
  // skill / New cron / Convert) — they still appear, read-only, in the
  // browse tree and the cron management list.
  const [coreAgents, setCoreAgents] = useState<string[]>([])
  const [convertOpen, setConvertOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [detailDirty, setDetailDirty] = useState(false)
  // Selector parity with the Workflows list: an agent filter, collapsible
  // per-agent groups, and a collapsible rail (« / ») for more editor width.
  // `scopedAgent` (ADR-111 IA-1) locks this permanently when set — see the
  // hidden filter dropdown below.
  const [agentFilter, setAgentFilter] = useState<string>(scopedAgent || initialAgent || 'all')
  const [collapsedAgents, setCollapsedAgents] = useState<Set<string>>(new Set())
  const [listCollapsed, setListCollapsed] = useState(false)
  const toggleAgent = (agent: string) =>
    setCollapsedAgents(prev => {
      const next = new Set(prev)
      if (next.has(agent)) next.delete(agent); else next.add(agent)
      return next
    })
  // --- Deep linking (ADR-111 IA-6) ------------------------------------------
  //
  // A skill is shared by copying the address bar, so the focused skill has to BE
  // in the address bar. Both halves are required and neither is sufficient: the
  // URL is rewritten as the operator moves (selectSkill below), and a pasted URL
  // resolves back to the skill (the effect after it).
  //
  // Skills were the facet this was missed on. The route
  // /fleet-editor/agents/:agent/:facet/:name already matched
  // .../skills/<slug> and rendered the Skills facet — it just dropped the slug on
  // the floor, so every skill on an agent had the same address and a shared link
  // landed the recipient on whatever the rail happened to select. A generic route
  // makes a deep link POSSIBLE; the facet still has to claim it.
  //
  // Only when scopedAgent is set. Unscoped, this browses every agent's skills and
  // a slug is not unique across them, so there is no honest URL to write.
  const skillPath = (slug?: string) =>
    `/fleet-editor/agents/${encodeURIComponent(scopedAgent ?? '')}/skills${slug ? `/${encodeURIComponent(slug)}` : ''}`
  // replace: focusing a skill is not a distinct "page", and pushing would make
  // browser Back walk the operator back through every row they clicked.
  const syncURL = (slug?: string) => {
    if (!scopedAgent) return
    navigate(skillPath(slug), { replace: true })
  }

  /** Focus a skill, and say so in the URL. */
  const selectSkill = (agent: string, slug: string) => {
    setNewSkill(null)
    setSelected({ agent, slug })
    syncURL(slug)
  }

  // Open the New-skill form, optionally pre-scoped to a specific agent (rail "＋").
  const openNewSkill = (agent?: string) => {
    setNewSkillAgent(agent)
    setNewSkillOpen(true)
    setConvertOpen(false)
  }

  useEffect(() => {
    let live = true
    api
      .listAgentSkills()
      .then(r => {
        if (!live) return
        const agents = r.agents ?? []
        setAgentSkills(agents)
        setLoading(false)
        // Sync (issue 1): if we arrived with an agent context, focus it and
        // auto-select its first skill so the editor isn't blank.
        if (initialAgent) {
          const a = agents.find(x => x.agent === initialAgent)
          const firstVisible = a ? a.skills.find(s => !isInheritedFromPlatform(s)) : undefined
          if (a && firstVisible) setSelected({ agent: a.agent, slug: firstVisible.slug })
        }
      })
      .catch(e => { if (live) { setLoadErr(em(e)); setLoading(false) } })
    // Full roster for the "＋ New skill" agent picker — includes agents that have
    // no skills yet (the exact case where you'd author their first one).
    api.listAgents()
      .then(r => { if (live) setRoster((r.agents ?? []).slice().sort()) })
      .catch(() => {})
    // ADR-103: the core-agent set, to exclude from authoring pickers.
    api.listCoreAgents()
      .then(r => { if (live) setCoreAgents(r.agents ?? []) })
      .catch(() => {})
    return () => { live = false }
    // initialAgent is the mount-time context; deliberately not re-run on change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Resolve a pasted /skills/:name once the list has loaded (ADR-111 IA-6).
  //
  // Guarded by a ref rather than by comparing to current state: after arriving on
  // a link the operator must stay free to click another skill, or ＋ New, without
  // this snapping them back to where they came in. It fires for the FIRST load
  // only.
  //
  // A slug that matches nothing is left alone — no redirect, no error. The skill
  // may be staged and not yet deployed, or the link may be stale; silently
  // rewriting the URL destroys the evidence of which link was shared, and an
  // operator who pasted one is better served by an intact address bar than by a
  // bounce they cannot read.
  const resolvedDeepLink = useRef(false)
  useEffect(() => {
    if (resolvedDeepLink.current || loading || !initialName || !scopedAgent) return
    resolvedDeepLink.current = true
    const mine = agentSkills.find(a => a.agent === scopedAgent)
    if (mine?.skills.some(s => s.slug === initialName)) {
      setNewSkill(null)
      setSelected({ agent: scopedAgent, slug: initialName })
    }
  }, [loading, initialName, scopedAgent, agentSkills])

  // ADR-101: when the /fleet checkout advances (published content changed — via
  // the ADR-100 timer or an ADR-101 webhook), refresh the lists so new/renamed/
  // deleted skills appear without a manual reload. SkillDetail handles the open
  // editor's own reaction (banner / reload).
  const fleetSha = useFleetVersion()
  const firstSha = useRef(true)
  useEffect(() => {
    if (!fleetSha) return
    if (firstSha.current) { firstSha.current = false; return }
    api.listAgentSkills().then(r => setAgentSkills(r.agents ?? [])).catch(() => {})
    api.listAgents().then(r => setRoster((r.agents ?? []).slice().sort())).catch(() => {})
  }, [fleetSha])

  // Every file in the overlay, flattened — SkillFiles narrows it to one skill by
  // prefix. Re-read when /fleet moves, since a deploy may have added or removed
  // a file inside a skill. A failed read leaves it null: the panel then says it
  // could not read the files rather than claiming the skill has none.
  useEffect(() => {
    let live = true
    api.getFleetTree()
      .then(t => { if (live) setTreeFiles((t.agents ?? []).flatMap(a => a.files ?? [])) })
      .catch(() => {})
    return () => { live = false }
  }, [fleetSha])

  // Agents present, for the filter dropdown (sorted).
  const agentOptions = useMemo(() => agentSkills.map(a => a.agent).sort(), [agentSkills])
  // ADR-103: the roster with core agents excluded — every "＋ New skill" /
  // "＋ New cron" agent picker uses this, not the raw roster (core agents are
  // still browsable, just not authorable).
  const authorableRoster = useMemo(() => roster.filter(a => !coreAgents.includes(a)), [roster, coreAgents])
  // Roster agents that have no skills yet — so you can author their first from
  // the rail (they never appear in the skills tree, which lists only agents with
  // ≥1 skill). Excludes core agents (they're never authored per-fleet). When
  // scoped to one agent, narrowed to just that agent (or empty).
  const agentsWithoutSkills = useMemo(
    () => authorableRoster.filter(a => !agentSkills.some(x => x.agent === a) && (!scopedAgent || a === scopedAgent)),
    [authorableRoster, agentSkills, scopedAgent],
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return agentSkills
      .filter(a => agentFilter === 'all' || a.agent === agentFilter)
      .map(a => ({
        ...a,
        inheritedCount: inheritedCount(a.skills),
        skills: q
          ? a.skills.filter(s => !isInheritedFromPlatform(s)).filter(s =>
              a.agent.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q) || s.name.toLowerCase().includes(q),
            )
          : a.skills.filter(s => !isInheritedFromPlatform(s)),
      }))
      .filter(a => a.skills.length > 0 || (!q && a.inheritedCount > 0))
  }, [agentSkills, search, agentFilter])

  // Guard against losing an unsaved draft when switching skills or leaving.
  const guardedNav = (fn: () => void) => {
    if (detailDirty && !window.confirm('Discard your unsaved skill edits?')) return
    setDetailDirty(false)
    fn()
  }
  const totalSkills = agentSkills.reduce((n, a) => n + a.skills.filter(s => !isInheritedFromPlatform(s)).length, 0)

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Skills</h1>
          <p className="text-sm text-gray-500 mt-1">
            A skill routes to its workflows. Edit the router, keep its <span className="font-mono">routes:</span> in sync, and propose changes for in-app review.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { if (newSkillOpen) { setNewSkillOpen(false) } else { openNewSkill(undefined) } }}
            className="px-3 py-1.5 text-sm border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50"
            title="Author a brand-new skill for an agent"
          >
            ＋ New skill
          </button>
          <button
            type="button"
            onClick={() => { setConvertOpen(v => !v); setNewSkillOpen(false) }}
            className="px-3 py-1.5 text-sm border border-purple-300 text-purple-700 rounded hover:bg-purple-50"
            title="Convert a skill into a leaner skill + workflows (ADR-080)"
          >
            ⤳ Convert skill
          </button>
        </div>
      </div>

      {loadErr && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{loadErr}</div>}

      {newSkillOpen && (
        <NewSkillForm
          key={newSkillAgent ?? 'any'}
          roster={authorableRoster}
          agentSkills={agentSkills}
          defaultAgent={newSkillAgent ?? (agentFilter !== 'all' ? agentFilter : initialAgent)}
          onCreate={(agent, slug) => {
            guardedNav(() => {
              setSelected(null)
              setNewSkill({ agent, slug, scaffold: newSkillScaffold(slug) })
              setNewSkillOpen(false)
              // Drop the previously-focused skill from the URL: the pane now
              // shows an unsaved scaffold, and leaving the old slug there would
              // make the address bar name something that is not on screen. The
              // NEW slug is deliberately not written either — it does not exist
              // until it is proposed and deployed, so a link to it would 404 for
              // whoever it was sent to.
              syncURL(undefined)
            })
          }}
          onClose={() => setNewSkillOpen(false)}
        />
      )}
      {convertOpen && <ConvertPanel canPropose={canPropose} onClose={() => setConvertOpen(false)} />}

      <div className="flex-1 min-h-0 grid gap-4" style={{ gridTemplateColumns: `${listCollapsed ? '40px' : '300px'} minmax(0,1fr)` }}>
        {/* Left: skills tree — collapsible rail, search + agent filter, collapsible groups */}
        {listCollapsed ? (
          <div className="border border-gray-200 rounded-lg bg-white flex flex-col items-center pt-2">
            <button type="button" onClick={() => setListCollapsed(false)} title="Show skills list"
              className="text-gray-400 hover:text-gray-700 text-sm">»</button>
          </div>
        ) : (
        <div className="border border-gray-200 rounded-lg bg-white flex flex-col overflow-hidden">
          <div className="p-2 border-b border-gray-100 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${totalSkills} skill${totalSkills === 1 ? '' : 's'}…`}
                className="flex-1 min-w-0 text-xs border border-gray-200 rounded px-2 py-1"
              />
              <button type="button" onClick={() => setListCollapsed(true)} title="Collapse the list for more editor width"
                className="shrink-0 text-gray-400 hover:text-gray-700 text-sm px-1">«</button>
            </div>
            {!scopedAgent && (
              <select
                value={agentFilter}
                onChange={e => setAgentFilter(e.target.value)}
                className="w-full text-xs border border-gray-200 rounded px-2 py-1 bg-white"
              >
                <option value="all">All agents ({agentOptions.length})</option>
                {agentOptions.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {loading ? (
              <p className="text-xs text-gray-400 p-4 animate-pulse">Loading skills…</p>
            ) : agentSkills.length === 0 ? (
              <p className="text-xs text-gray-500 p-4">No agent has any skills yet.</p>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-gray-400 p-4">No skills match the current filter.</p>
            ) : (
              <ul className="p-2 space-y-1">
                {filtered.map(a => {
                  const agentCollapsed = collapsedAgents.has(a.agent)
                  return (
                    <li key={a.agent}>
                      <div className="group flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleAgent(a.agent)}
                          className="flex-1 min-w-0 flex items-center gap-1 px-1 py-0.5 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600"
                        >
                          <span className="text-[9px] w-2">{agentCollapsed ? '▸' : '▾'}</span>
                          <span className="truncate">{a.agent}</span>
                          <span className="ml-1 font-normal normal-case text-gray-300">({a.skills.length})</span>
                        </button>
                        {a.core ? (
                          <span className="shrink-0 text-[9px] normal-case font-normal text-gray-400 italic">core-owned · read-only</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openNewSkill(a.agent)}
                            title={`New skill for ${a.agent}`}
                            className="shrink-0 px-1 text-emerald-500 opacity-0 group-hover:opacity-100 hover:text-emerald-700 text-sm"
                          >
                            ＋
                          </button>
                        )}
                      </div>
                      {!agentCollapsed && (
                        <ul className="mt-0.5">
                          {a.skills.map(s => {
                            const isSel = selected?.agent === a.agent && selected?.slug === s.slug
                            // ADR-111 D2: SkillInfo has no path (only `slug`),
                            // so this matches by PREFIX against the skill's
                            // whole directory — any file inside it (SKILL.md,
                            // referenced resources, etc.) staged counts.
                            const isStaged = isPrefixStaged(stagedPaths, `agents/${a.agent}/skills/${s.slug}/`)
                            return (
                              <li key={s.slug}>
                                <button
                                  type="button"
                                  onClick={() => guardedNav(() => selectSkill(a.agent, s.slug))}
                                  className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-1.5 ${isSel ? 'bg-indigo-50 text-indigo-800' : 'hover:bg-gray-50 text-gray-700'}`}
                                >
                                  <span className="font-mono truncate">{s.slug}</span>
                                  <span className="ml-auto shrink-0 flex items-center gap-1">
                                    {isStaged && <StagedBadge />}
                                    {s.route_count > 0 && (
                                      <span
                                        className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700"
                                        title={`${s.route_count} route${s.route_count === 1 ? '' : 's'} → workflows (this is a router skill)`}
                                      >
                                        ⇢ {s.route_count}
                                      </span>
                                    )}
                                  </span>
                                </button>
                              </li>
                            )
                          })}
                          {a.inheritedCount > 0 && (
                            <li className="mx-2 my-2 rounded border border-gray-200 bg-gray-50 px-2.5 py-2 text-xs text-gray-600">
                              <div className="flex items-center gap-1.5 font-medium text-gray-700">
                                <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" stroke="currentColor" strokeWidth="1.5">
                                  <rect x="3" y="7" width="10" height="7" rx="1.5" />
                                  <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
                                </svg>
                                <span>Inherited from platform</span>
                              </div>
                              <p className="mt-1">
                                {a.inheritedCount} universal skill{a.inheritedCount === 1 ? '' : 's'} composed onto this agent at deploy.
                              </p>
                              <Link className="mt-1 inline-block text-indigo-600 hover:text-indigo-800" to={PLATFORM_SKILLS_PATH}>
                                View platform skills →
                              </Link>
                            </li>
                          )}
                        </ul>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
          {/* Agents with no skills yet — author their first from here. */}
          {!loading && (agentFilter === 'all' || !!scopedAgent) && !search.trim() && agentsWithoutSkills.length > 0 && (
            <div className="border-t border-gray-100 p-2">
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">No skills yet</div>
              <ul className="space-y-0.5">
                {agentsWithoutSkills.map(a => (
                  <li key={a} className="group flex items-center gap-1">
                    <span className="flex-1 min-w-0 truncate px-1 text-xs text-gray-400">{a}</span>
                    <button
                      type="button"
                      onClick={() => openNewSkill(a)}
                      title={`New skill for ${a}`}
                      className="shrink-0 px-1 text-emerald-500 opacity-0 group-hover:opacity-100 hover:text-emerald-700 text-sm"
                    >
                      ＋
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        )}

        {/* Right: new-skill editor / selected skill detail / editor */}
        <div className="border border-gray-200 rounded-lg bg-white overflow-auto p-4">
          {newSkill ? (
            <SkillDetail
              key={`new/${newSkill.agent}/${newSkill.slug}`}
              agent={newSkill.agent}
              slug={newSkill.slug}
              scaffold={newSkill.scaffold}
              canPropose={canPropose}
              onOpenWorkflow={onOpenWorkflow}
              onDirtyChange={setDetailDirty}
              treeFiles={treeFiles}
              stagedPaths={stagedPaths}
              onExit={() => { setNewSkill(null); setDetailDirty(false) }}
            />
          ) : selected ? (
            <SkillDetail
              key={`${selected.agent}/${selected.slug}`}
              agent={selected.agent}
              slug={selected.slug}
              canPropose={canPropose}
              onOpenWorkflow={onOpenWorkflow}
              onDirtyChange={setDetailDirty}
              treeFiles={treeFiles}
              stagedPaths={stagedPaths}
              fleetSha={fleetSha}
              // ADR-103: core-owned when the agent itself is core, or this
              // particular skill shadows a universal name.
              isCoreOwned={
                agentSkills.find(a => a.agent === selected.agent)?.core ||
                agentSkills.find(a => a.agent === selected.agent)?.skills.find(s => s.slug === selected.slug)?.core ||
                false
              }
            />
          ) : (
            <p className="text-sm text-gray-400">Select a skill to view its routes and edit it, or ＋ New skill to author one.</p>
          )}
        </div>
      </div>
    </div>
  )
}

// NewSkillForm — pick an agent + a name, then open the scaffold editor. Slug is
// derived from the name (the validator requires name == slug), and we block a
// name that collides with the agent's existing skills.
function NewSkillForm({
  roster,
  agentSkills,
  defaultAgent,
  onCreate,
  onClose,
}: {
  roster: string[]
  agentSkills: AgentSkills[]
  defaultAgent?: string
  onCreate: (agent: string, slug: string) => void
  onClose: () => void
}) {
  const [agent, setAgent] = useState(defaultAgent && roster.includes(defaultAgent) ? defaultAgent : (roster[0] ?? ''))
  const [name, setName] = useState('')
  const slug = slugifyWorkflowName(name)
  const existing = new Set((agentSkills.find(a => a.agent === agent)?.skills ?? []).map(s => s.slug))
  const dup = slug !== '' && existing.has(slug)
  const valid = agent !== '' && slug !== '' && !dup

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-4 py-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-emerald-800">New skill</h3>
        <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:text-gray-800">✕ close</button>
      </div>
      <p className="text-xs text-gray-600">
        Pick the agent and a name. You&apos;ll land in the editor with a starter <span className="font-mono">SKILL.md</span> — the copilot and routes editor help you fill it in, and proposing sends it for in-app review (nothing ships until you approve it).
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-gray-600 block">Agent
          <select value={agent} onChange={e => setAgent(e.target.value)}
            className="mt-1 block text-sm border border-gray-300 rounded px-2 py-1 min-w-[12rem]">
            {roster.length === 0 && <option value="">(loading agents…)</option>}
            {roster.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-600 block flex-1 min-w-[14rem]">Skill name
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && valid) onCreate(agent, slug) }}
            placeholder="e.g. Draft weekly summary"
            className="mt-1 block w-full text-sm border border-gray-300 rounded px-2 py-1" />
        </label>
        <button type="button" disabled={!valid} onClick={() => onCreate(agent, slug)}
          className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
          Open editor →
        </button>
      </div>
      {slug !== '' && (
        <p className="text-[11px] text-gray-500">
          Path: <span className="font-mono">agents/{agent}/skills/{slug}/SKILL.md</span>
          {dup && <span className="text-red-600"> — {agent} already has a skill named “{slug}”.</span>}
        </p>
      )}
    </div>
  )
}

// SkillDetail — view + edit one skill. Remounts (via key) when the selection
// changes, so its draft/edit state resets cleanly.
function SkillDetail({
  agent,
  slug,
  canPropose,
  onOpenWorkflow,
  onDirtyChange,
  scaffold,
  onExit,
  fleetSha,
  isCoreOwned,
  treeFiles,
  stagedPaths,
}: {
  agent: string
  slug: string
  canPropose: boolean
  onOpenWorkflow: (agent: string, name: string, edit?: boolean) => void
  onDirtyChange: (dirty: boolean) => void
  // Every overlay file, read once by the list component; SkillFiles narrows it
  // to this skill. Null while it is still loading.
  treeFiles: FleetFileNode[] | null
  stagedPaths: Set<string>
  // When set, this is a brand-new skill: skip the (nonexistent) server load, open
  // straight into the editor seeded with this scaffold, and Propose CREATES the
  // file (proposeSkill commits with an empty prior-SHA when the path is absent).
  // onExit closes the new-skill editor (parent clears its `newSkill` state).
  scaffold?: string
  onExit?: () => void
  // ADR-101: the /fleet commit SHA (polled by the parent). When it changes, this
  // skill may have changed on the server — reload it if there's no unsaved draft,
  // else surface a reload banner (never clobber in-progress edits).
  fleetSha?: string
  // ADR-103: true when this skill is core-owned (its agent is core, or it
  // shadows a universal name) — hides Edit/rename/delete; a brand-new skill
  // is never core-owned (its agent picker already excludes core agents).
  isCoreOwned?: boolean
}) {
  const notifyAttention = useAttentionNotify()
  const isNew = scaffold !== undefined
  const [skill, setSkill] = useState<SkillContent | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [health, setHealth] = useState<SkillValidation | null>(null) // view-mode validation of the saved skill
  const [agentActivities, setAgentActivities] = useState<ActivitySummary[]>([]) // this agent's activities (route targets + orphan detection)
  const [editing, setEditing] = useState(isNew)
  const [draft, setDraft] = useState(scaffold ?? '')
  const [validation, setValidation] = useState<SkillValidation | null>(null)
  const [validating, setValidating] = useState(false)
  const [chat, setChat] = useState<CopilotMessage[]>([])
  const [chatInput, setChatInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [proposing, setProposing] = useState(false)
  const proposingRef = useRef(false)
  const [proposeMsg, setProposeMsg] = useState('')
  const [proposed, setProposed] = useState<ProposeSkillResult | null>(null)
  const [pendingChange, setPendingChange] = useState<SkillChangePendingError | null>(null)
  const [err, setErr] = useState<string | null>(null)
  // ADR-107 P3: the fleet's own author tests failed against this proposed
  // change (409 author_tests_failing) — rendered via the SAME FailingChecksPanel
  // ADR-1027's PR review uses, not a generic error toast. The draft stays put
  // so the operator can fix + retry.
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  const [blockedRefs, setBlockedRefs] = useState<DeleteEdge[]>([])
  const [showDiff, setShowDiff] = useState(false)
  // Files inside this skill marked for removal. Independent of edit mode: the
  // whole point is that a deletion is proposable without touching SKILL.md.
  const [markedForDelete, setMarkedForDelete] = useState<string[]>([])
  const [externalChanged, setExternalChanged] = useState(false) // ADR-101: published version differs from what's loaded
  // ADR-121 D1: the copilot chat is the default/primary edit surface; the raw
  // SKILL.md textarea is demoted behind this "Raw / advanced" toggle — fully
  // reachable (ADR-121 D6, the on-disk SoR), never shown first.
  const [showRaw, setShowRaw] = useState(false)
  // ADR-121 D2 — the AI Critique action: grounded, read-only review returning
  // dismissible suggestions. Never writes; "ask the copilot" seeds the SAME
  // copilot chat above so any resulting edit flows through the normal
  // validate -> propose -> Approve&Deploy path.
  const [critique, setCritique] = useState<SkillCritiqueResponse | null>(null)
  const [critiqueOpen, setCritiqueOpen] = useState(false)
  const [critiqueBusy, setCritiqueBusy] = useState(false)
  const [critiqueErr, setCritiqueErr] = useState<string | null>(null)
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<number>>(new Set())
  // A suggestion queued to seed the copilot chat once edit mode is actually
  // entered — avoids racing the startEdit() state update (draft isn't
  // populated with the saved skill until that render commits).
  const [pendingCopilotSeed, setPendingCopilotSeed] = useState<string | null>(null)

  // Load the skill, then validate it (saved state) for the view-mode health badge.
  // A brand-new skill has no file to load: synthesize an empty saved-state so the
  // editor renders, and let the draft-validation effect drive the badge instead.
  useEffect(() => {
    let live = true
    if (isNew) {
      setSkill({ agent, slug, name: slug, description: '', body: '', raw: scaffold ?? '', routes: [], mcp: [], mcp_tools: [], found: false })
    } else {
      api
        .getSkill(agent, slug)
        .then(s => {
          if (!live) return
          setSkill(s)
          api.validateSkill(agent, slug, s.raw).then(v => { if (live) setHealth(v) }).catch(() => {})
        })
        .catch(e => { if (live) setLoadErr(em(e)) })
    }
    // This agent's activities — the valid route targets + orphan detection.
    api.listActivities()
      .then(r => { if (live) setAgentActivities(r.activities.filter(a => a.agent === agent).sort((x, y) => x.name.localeCompare(y.name))) })
      .catch(() => {})
    return () => { live = false }
  }, [agent, slug, isNew, scaffold])

  // For a new skill, count from the scaffold so leaving before any edit still warns.
  //
  // Staged deletions count too, and NOT only in edit mode. Left as
  // `editing && …` they would be dropped silently by a navigation that gives a
  // draft edit a confirm dialog — the same work, two different answers.
  const dirty =
    (editing && !!skill && (isNew ? !proposed : draft !== skill.raw)) ||
    (markedForDelete.length > 0 && !proposed)
  useEffect(() => { onDirtyChange(dirty) }, [dirty, onDirtyChange])

  // ADR-101: reload the saved skill from the server (published version) and rebase
  // the editor draft on it.
  const reloadFromServer = () => {
    api.getSkill(agent, slug).then(s => {
      setSkill(s)
      setExternalChanged(false)
      if (editing) setDraft(s.raw)
      api.validateSkill(agent, slug, s.raw).then(setHealth).catch(() => {})
    }).catch(e => setErr(em(e)))
  }

  // React to a /fleet version bump (published content changed): if there's no
  // unsaved draft, reload silently; otherwise flag a banner and let the operator
  // reload on their terms — never discard in-progress edits.
  const firstVer = useRef(true)
  useEffect(() => {
    if (isNew || !fleetSha) return
    if (firstVer.current) { firstVer.current = false; return }
    if (dirty) setExternalChanged(true)
    else reloadFromServer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetSha])

  // Debounced auto-validation of the draft while editing — so Propose enables
  // without a manual "Validate" click after every keystroke.
  useEffect(() => {
    if (!editing) return
    const raw = draft
    setValidating(true)
    const h = setTimeout(() => {
      api.validateSkill(agent, slug, raw)
        .then(v => setValidation(v))
        .catch(e => setErr(em(e)))
        .finally(() => setValidating(false))
    }, 600)
    return () => clearTimeout(h)
  }, [draft, editing, agent, slug])

  const startEdit = () => {
    setDraft(skill?.raw ?? '')
    setValidation(null); setValidating(false); setChat([]); setProposed(null); setErr(null)
    setShowRaw(false) // ADR-121 D1: always re-enter on the copilot, not the raw editor
    setEditing(true)
  }
  const cancelEdit = () => {
    if (dirty && !window.confirm(isNew ? 'Discard this new skill?' : 'Discard your unsaved skill edits?')) return
    onDirtyChange(false)
    // A new skill has no saved view to fall back to — leaving edit mode closes it.
    if (isNew) { onExit?.(); return }
    setEditing(false)
  }

  const sendChat = async (explicit?: string) => {
    if (proposingRef.current) return
    const msg = (explicit ?? chatInput).trim()
    if (!msg) return
    const next = [...chat, { role: 'user' as const, content: msg }]
    setChat(next); setChatInput(''); setBusy(true); setErr(null)
    try {
      const r = await api.skillCopilot(agent, slug, next, draft)
      setDraft(r.draft || draft)
      setValidation(r.validation)
      setChat([...next, { role: 'assistant', content: r.rationale || '(updated the skill)' }])
      notifyAttention({ id: copilotJobAttentionId(r, 'skill-copilot'), source: 'skill-copilot', reason: 'new-reply' })
    } catch (e) {
      setErr(em(e))
      if (!(e instanceof UnauthorizedError)) {
        notifyAttention({ id: localAttentionId('skill-copilot'), source: 'skill-copilot', reason: 'new-reply' })
      }
    }
    finally { setBusy(false) }
  }

  // ADR-121 D2 — run the Critique action against whatever's currently shown
  // (the live draft while editing, else the saved skill), never a write.
  const runCritique = async () => {
    setCritiqueBusy(true); setCritiqueErr(null)
    try {
      const content = editing ? draft : (skill?.raw ?? '')
      const r = await api.critiqueSkill(agent, slug, content)
      setCritique(r)
      setDismissedSuggestions(new Set())
      setCritiqueOpen(true)
    } catch (e) {
      setCritiqueErr(e instanceof CopilotNotConfiguredError ? "The AI critique isn't configured for this fleet." : em(e))
    } finally {
      setCritiqueBusy(false)
    }
  }

  // "Ask the copilot to apply this" — never edits directly (ADR-121 D5):
  // enters edit mode if needed and seeds the SAME skill copilot chat with the
  // suggestion, so any resulting draft still goes through validate -> propose.
  const askCopilotToApply = (s: SkillCritiqueSuggestion) => {
    if (proposingRef.current) return
    setPendingCopilotSeed(
      `Apply this critique suggestion — [${s.area}/${s.severity}] ${s.finding} Suggested fix: ${s.suggestion}`,
    )
    if (!editing) startEdit()
  }

  // Fires once edit mode (and its freshly-loaded draft) has actually
  // committed, so sendChat's closure sees the right `draft` instead of a
  // stale '' from before startEdit()'s state update landed.
  useEffect(() => {
    if (editing && pendingCopilotSeed && !proposing) {
      const msg = pendingCopilotSeed
      setPendingCopilotSeed(null)
      void sendChat(msg)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, pendingCopilotSeed, proposing])

  const propose = async () => {
    if (proposingRef.current) return
    proposingRef.current = true
    setProposing(true)
    setPendingChange(null)
    setErr(null); setFailingChecks([]); setBlockedRefs([])
    try {
      // The SKILL.md write is sent only when there IS one. Outside edit mode
      // `draft` is empty, and the server now refuses a changeset that changes
      // nothing — so sending it unconditionally would turn every delete-only
      // proposal into a refusal.
      const content = editsSkillMD ? draft : ''
      const r = await api.proposeSkill(
        agent, slug, content, proposeMsg.trim() || undefined,
        markedForDelete.map(path => ({ path, op: 'delete' as const })),
      )
      setProposed(r)
      setMarkedForDelete([])
      onDirtyChange(false)
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else if (parseDeleteBlocked(e)?.blocked.length) setBlockedRefs(parseDeleteBlocked(e)!.blocked)
      else {
        const pending = parseSkillChangePending(e)
        if (pending) setPendingChange(pending)
        else setErr(em(e))
      }
    }
    finally {
      proposingRef.current = false
      setProposing(false)
    }
  }

  const draftValid = validation?.ok === true
  // Is there a SKILL.md edit in this proposal at all? A new skill always writes
  // one; an existing skill only when the draft differs from what is saved.
  const editsSkillMD = editing && (isNew || draft !== (skill?.raw ?? ''))
  // A valid draft, OR no draft edit plus at least one staged deletion.
  // `draftValid` alone is not enough: it is populated only by the edit-mode
  // validate effect, so outside edit mode it is permanently false and the
  // button would render disabled forever.
  const canSubmit = editsSkillMD ? draftValid : markedForDelete.length > 0

  if (loadErr) return <p className="text-sm text-red-600">Couldn&apos;t load skill: {loadErr}</p>
  if (!skill) return <p className="text-sm text-gray-400 animate-pulse">Loading {slug}…</p>

  // `?? []`: the backend serializes a routeless skill's `routes` as JSON null
  // (Go nil slice), so in view mode `liveRoutes.map(...)` below would throw and
  // blank the whole view when selecting any skill that has no routes.
  const liveRoutes = editing ? parseRoutesFromRaw(draft) : (skill.routes ?? [])
  const workflowOptions = agentActivities.map(a => a.name)
  const applyRoutes = (next: SkillRoute[]) => setDraft(setRoutesInSkillMd(draft, next))
  // Orphan fix-its: this agent's activities that HAVE triggers but no route
  // points at them yet — computed client-side from listActivities (ActivitySummary
  // carries triggers), so no backend round-trip.
  const routedSet = new Set(liveRoutes.map(r => r.workflow))
  const unrouted = agentActivities.filter(a => (a.triggers?.length ?? 0) > 0 && !routedSet.has(a.name))
  const filesPanel = (
    <SkillFiles
      agent={agent}
      slug={slug}
      tree={treeFiles}
      staged={stagedPaths}
      marked={markedForDelete}
      onChange={setMarkedForDelete}
      // The DRAFT while editing, so removing a reference and the file it points
      // at in one change does not warn about the reference it just deleted.
      skillText={editing ? draft : skill.raw}
      canDelete={canPropose && !isCoreOwned}
      disabled={busy || proposing || !!proposed}
    />
  )

  // The propose surface, defined once and rendered in both arms of the
  // view/edit split. It used to live inside the editing arm, which is why a
  // deletion — a complete proposal on its own — could not be sent at all.
  const proposeControls = (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {canPropose ? (
          <button type="button" disabled={busy || proposing || validating || !canSubmit || !!proposed} onClick={() => void propose()}
            className="text-xs px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-50"
            title={!canSubmit ? (editsSkillMD ? 'The skill must pass validation before it can be proposed' : 'Mark a file for removal, or edit the skill') : undefined}>
            {proposing ? 'Running checks…' : 'Propose change'}
          </button>
        ) : <span className="text-xs text-gray-500">Only a fleet owner can propose.</span>}
        {editing && (
          <button type="button" onClick={cancelEdit} className="text-xs px-3 py-1 text-gray-500 hover:text-gray-700">Cancel</button>
        )}
        {dirty && <span className="text-[10px] text-amber-600">unsaved edits</span>}
      </div>

      {canPropose && (
        <input type="text" value={proposeMsg} onChange={e => setProposeMsg(e.target.value)}
          placeholder="Optional note for the PR / reviewer" className="w-full text-xs border border-gray-200 rounded px-2 py-1" />
      )}

      <ValidationBlock validation={validation} />

      {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}
      {blockedRefs.length > 0 && <DeleteBlockedPanel blocked={blockedRefs} />}
      {pendingChange && <SkillChangePendingPanel change={pendingChange} />}

      {proposed && (
        proposed.improvement_warning ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
            <p>{proposed.improvement_warning}</p>
            {proposed.gate === 'inconclusive' && <GateInconclusiveNote className="text-amber-800" reason={proposed.gate_reason} />}
          </div>
        ) : (
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-1">
            <p>Sent for review — {isNew ? 'the new skill' : 'your skill changes'}. Nothing ships until you approve it.</p>
            {proposed.gate === 'inconclusive' && <GateInconclusiveNote className="text-amber-800" reason={proposed.gate_reason} />}
            {proposed.improvement_id
              ? <Link to={`/improvements/${encodeURIComponent(proposed.improvement_id)}/pr`} className="font-medium underline">Review &amp; approve in Mission Control →</Link>
              : <span>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</span>}
          </div>
        )
      )}
    </div>
  )

  const diff = editing && dirty ? lineDiff(skill.raw, draft) : []
  const diffAdds = diff.filter(d => d.type === 'add').length
  const diffDels = diff.filter(d => d.type === 'del').length

  return (
    <div className="space-y-4">
      {externalChanged && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center justify-between gap-3">
          <span>A newer version of this skill was published to the fleet source. Your unsaved edits are preserved.</span>
          <button
            type="button"
            onClick={() => { if (!dirty || window.confirm('Discard your unsaved edits and load the published version?')) reloadFromServer() }}
            className="shrink-0 px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700"
          >
            Reload published version
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 font-mono truncate">{skill.slug}</h2>
            {isNew && <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">＋ new skill</span>}
            {isCoreOwned && <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 italic">core-owned · read-only</span>}
            {!editing && <HealthBadge v={health} />}
            {editing && <span className="text-xs text-gray-400">{validating ? 'validating…' : draftValid ? '✓ valid' : validation ? '✗ has errors' : ''}</span>}
          </div>
          <p className="text-xs text-gray-500 truncate">{agent}{skill.description ? ` — ${skill.description}` : ''}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            disabled={critiqueBusy}
            onClick={() => void runCritique()}
            className="shrink-0 px-3 py-1.5 text-sm border border-purple-300 text-purple-700 rounded hover:bg-purple-50 disabled:opacity-50"
            title="Ask the AI to review this skill and suggest improvements — read-only, nothing is changed"
          >
            {critiqueBusy ? 'Critiquing…' : '🔍 Critique'}
          </button>
          {!editing && !isCoreOwned && (
            <button type="button" onClick={startEdit} className="shrink-0 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700">
              Edit
            </button>
          )}
        </div>
      </div>

      {critiqueErr && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{critiqueErr}</p>}
      {critiqueOpen && critique && (
        <CritiquePanel
          critique={critique}
          dismissed={dismissedSuggestions}
          onDismiss={i => setDismissedSuggestions(prev => new Set(prev).add(i))}
          onClose={() => setCritiqueOpen(false)}
          onAskCopilot={askCopilotToApply}
          canAskCopilot={!isCoreOwned && !proposing}
        />
      )}

      {/* Manage (rename / delete) — view mode only, fleet_owner only, never for
          a not-yet-created skill, and never for a core-owned one (ADR-103 hard
          lock: changes to it flow only through core). Both route through
          propose -> PR -> approve. */}
      {!editing && !isNew && !isCoreOwned && canPropose && <SkillManage agent={agent} slug={slug} />}

      {/* Routes — the skill→workflows one-to-many. Structured editor while editing
          (writes back into the draft frontmatter), read-only tree in view mode. */}
      {editing ? (
        <>
          <RoutesEditor
            routes={liveRoutes}
            workflowOptions={workflowOptions}
            onChange={applyRoutes}
            onOpenWorkflow={name => {
              if (dirty && !window.confirm('Discard your unsaved skill edits and open the workflow to edit?')) return
              onDirtyChange(false)
              onOpenWorkflow(agent, name, true)
            }}
          />
          {unrouted.length > 0 && (
            <UnroutedFixits
              activities={unrouted}
              onAdd={(name, intent) => applyRoutes([...liveRoutes, { intent, workflow: name }])}
            />
          )}
        </>
      ) : (
        <RoutesTable routes={liveRoutes} onOpenWorkflow={n => onOpenWorkflow(agent, n)} />
      )}

      {/* Saved-skill health detail (view mode) — surfaces dangling routes etc. */}
      {!editing && health && (!health.ok || (health.warnings && health.warnings.length > 0)) && (
        <ValidationBlock validation={health} />
      )}

      {!editing ? (
        <div className="space-y-3">
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">SKILL.md</div>
            <pre className="text-xs text-gray-700 bg-gray-50 border border-gray-100 rounded p-2 whitespace-pre-wrap break-words max-h-[28rem] overflow-auto font-mono">{skill.raw}</pre>
          </div>
          {!isNew && filesPanel}
          {/* The propose surface lives in BOTH arms now. A deletion is a complete
              proposal on its own, and while this block sat inside the editing
              arm there was no way to send one without also editing SKILL.md. */}
          {(markedForDelete.length > 0 || proposed || pendingChange || failingChecks.length > 0 || blockedRefs.length > 0) && proposeControls}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Copilot chat */}
          <div className="rounded border border-indigo-100 bg-indigo-50/40 p-2 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-indigo-700">Skill copilot — ask it to format, resolve ambiguity, or wire routes</div>
              <button
                type="button"
                disabled={busy || proposing}
                onClick={() => void sendChat(WIRE_ROUTES_PROMPT)}
                className="shrink-0 text-[11px] px-2 py-0.5 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                title="Ask the copilot to generate/repair routes from this agent's workflows"
              >
                ⚙ Wire/repair routes
              </button>
            </div>
            {chat.length > 0 && (
              <div className="space-y-1 max-h-40 overflow-auto">
                {chat.map((m, i) => (
                  <div key={i} className={`text-xs rounded px-2 py-1 ${m.role === 'user' ? 'bg-white border border-gray-200' : 'bg-indigo-100 text-indigo-900'}`}>
                    <span className="font-medium">{m.role === 'user' ? 'You' : 'Copilot'}:</span> {m.content}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !busy && !proposing) { e.preventDefault(); void sendChat() } }}
                placeholder="e.g. tidy the routing and flag any ambiguous intents"
                className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
              />
              <button type="button" disabled={busy || proposing || !chatInput.trim()} onClick={() => void sendChat()}
                className="text-xs px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-50">{busy ? '…' : 'Send'}</button>
            </div>
          </div>

          {/* Raw editor — full SKILL.md source; the routes editor and copilot above
              both write into it. ADR-121 D1/D6: demoted behind this toggle — the
              copilot is the default surface, but the raw source stays fully
              reachable (it's the on-disk SoR) and is never removed. */}
          <div>
            <button
              type="button"
              onClick={() => setShowRaw(v => !v)}
              className="text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600"
              title="Edit the full SKILL.md source directly — the escape-hatch behind the copilot"
            >
              {showRaw ? '▾' : '▸'} Raw / advanced (SKILL.md source)
            </button>
          </div>
          {showRaw && (
            /* ADR-127 D9-D15 — the reference picker, on the surface where the
               hardcoded GIDs this programme exists to remove actually live. The
               slot is `skill.body`, which is what the validator already collects
               references under: scalars only (ADR-126 D7 forbids a whole
               collection in a prose brief) and LITERAL keys only, because a skill
               has no `inputs:` for a `by_key[...]` operand to read from.
               `onEnsurePin` writes the frontmatter pin the reference needs, or
               refuses — an unpinned reference is a validation error, not a
               warning. */
            <DataRefFieldAffordance
              slot="skill.body"
              onEnsurePin={pin => {
                const result = ensureDataPinInSkillMd(draft, pin.alias, pin.set)
                if (result.refusal) return result.refusal
                setDraft(result.raw)
                return null
              }}
              onChange={value => setDraft(value)}
            >
              {ref => (
                <textarea
                  ref={ref as React.RefObject<HTMLTextAreaElement>}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  rows={16}
                  className="w-full text-xs font-mono border border-gray-300 rounded px-2 py-1"
                />
              )}
            </DataRefFieldAffordance>
          )}

          {/* Review changes vs the saved skill (or scaffold, for a new skill) before proposing */}
          {diff.length > 0 && (
            <div className="rounded border border-gray-200">
              <button
                type="button"
                onClick={() => setShowDiff(v => !v)}
                className="w-full flex items-center gap-2 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
              >
                <span className="text-[9px] w-2">{showDiff ? '▾' : '▸'}</span>
                Review changes
                <span className="text-green-600">+{diffAdds}</span>
                <span className="text-red-600">−{diffDels}</span>
                <span className="text-gray-400">vs {isNew ? 'scaffold' : 'saved'}</span>
              </button>
              {showDiff && <DiffView diff={diff} />}
            </div>
          )}

          {!isNew && filesPanel}

          {proposeControls}
        </div>
      )}

      {err && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{err}</p>}
    </div>
  )
}

function SkillChangePendingPanel({ change }: { change: SkillChangePendingError }) {
  return (
    <div data-testid="skill-change-pending" className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
      <p className="whitespace-pre-wrap break-words">
        {change.message}
      </p>
      {change.improvementId && (
        <Link to={`/improvements/${encodeURIComponent(change.improvementId)}/pr`} className="block font-medium underline">
          Review the waiting change in Mission Control →
        </Link>
      )}
      {change.prNumber > 0 && change.prUrl && (
        <a href={change.prUrl} target="_blank" rel="noreferrer" className="block font-medium underline">
          Pull request #{change.prNumber}
        </a>
      )}
    </div>
  )
}

// SkillManage — the rename / delete affordance for a saved skill. Both actions
// go through propose -> PR -> in-app improvement (a skill only changes via
// commit+deploy, ADR-010), so the success state links to the same review flow.
function SkillManage({ agent, slug }: { agent: string; slug: string }) {
  const [mode, setMode] = useState<'none' | 'rename'>('none')
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  const [blockedRefs, setBlockedRefs] = useState<DeleteEdge[]>([])
  const [result, setResult] = useState<ProposeSkillResult | null>(null)
  const newSlug = slugifyWorkflowName(newName)
  const renameValid = newSlug !== '' && newSlug !== slug

  const run = async (fn: () => Promise<ProposeSkillResult>) => {
    setBusy(true); setErr(null); setFailingChecks([]); setBlockedRefs([])
    try { setResult(await fn()) } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else if (parseDeleteBlocked(e)?.blocked.length) setBlockedRefs(parseDeleteBlocked(e)!.blocked)
      else setErr(em(e))
    } finally { setBusy(false) }
  }

  if (result) {
    return result.improvement_warning ? (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
        <p>{result.improvement_warning}</p>
      </div>
    ) : (
      <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-1">
        <p>Sent for review. Nothing changes until you approve it.</p>
        {result.improvement_id
          ? <Link to={`/improvements/${encodeURIComponent(result.improvement_id)}/pr`} className="font-medium underline">Review &amp; approve in Mission Control →</Link>
          : <span>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</span>}
      </div>
    )
  }

  return (
    <div className="rounded border border-gray-200 bg-gray-50/60 px-3 py-2 text-xs space-y-2">
      {mode === 'none' && (
        // flex-wrap: the opened DeletePanel takes a line of its own below Manage/Rename.
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-gray-400 uppercase tracking-wider font-semibold text-[10px]">Manage</span>
          <button type="button" onClick={() => { setMode('rename'); setNewName(''); setErr(null) }}
            className="text-gray-600 hover:text-gray-900 underline">Rename</button>
          {/* ADR-123: delete goes through the dependency-aware panel, not a bare
              propose. A skill's routed workflows must cascade or its dispatch
              triggers are silently orphaned (ADR-097), which the old one-shot
              delete could not see. */}
          <DeletePanel target={{ kind: 'skill', agent, name: slug }} label="Delete" />
        </div>
      )}

      {mode === 'rename' && (
        <div className="space-y-2">
          <label className="block text-gray-600">Rename skill
            <input type="text" value={newName} autoFocus onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && renameValid && !busy) void run(() => api.renameSkill(agent, slug, newSlug)) }}
              placeholder="new name" className="mt-1 block w-full text-sm border border-gray-300 rounded px-2 py-1" />
          </label>
          {newSlug !== '' && (
            <p className="text-[11px] text-gray-500">
              New path: <span className="font-mono">agents/{agent}/skills/{newSlug}/</span>
              {newSlug === slug && <span className="text-amber-600"> — same as the current name.</span>}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button type="button" disabled={busy || !renameValid}
              onClick={() => void run(() => api.renameSkill(agent, slug, newSlug))}
              className="px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-50">{busy ? 'Running checks…' : 'Propose rename'}</button>
            <button type="button" onClick={() => setMode('none')} className="text-gray-500 hover:text-gray-700">Cancel</button>
          </div>
        </div>
      )}

      {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}
      {blockedRefs.length > 0 && <DeleteBlockedPanel blocked={blockedRefs} />}
      {err && <p className="text-red-600 whitespace-pre-wrap break-words">{err}</p>}
    </div>
  )
}

function HealthBadge({ v }: { v: SkillValidation | null }) {
  if (!v) return null
  if (!v.ok) {
    return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700" title={v.errors.join('; ')}>✗ {v.errors.length} error{v.errors.length === 1 ? '' : 's'}</span>
  }
  if (v.warnings && v.warnings.length > 0) {
    return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" title={v.warnings.join('; ')}>⚠ {v.warnings.length}</span>
  }
  return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">✓ valid</span>
}

// RoutesEditor — the structured routes editor (edit mode). Reads the routes
// parsed from the current draft and, on any change, writes the whole set back
// into the draft's frontmatter (via setRoutesInSkillMd) so the raw source stays
// the single source of truth. `inputs` on a route are preserved through edits
// (spread) even though they're only editable via the raw source.
function RoutesEditor({ routes, workflowOptions, onChange, onOpenWorkflow }: {
  routes: SkillRoute[]
  workflowOptions: string[]
  onChange: (routes: SkillRoute[]) => void
  onOpenWorkflow: (workflow: string) => void
}) {
  const update = (i: number, patch: Partial<SkillRoute>) => onChange(routes.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  const remove = (i: number) => onChange(routes.filter((_, idx) => idx !== i))
  const add = () => onChange([...routes, { intent: '', workflow: workflowOptions[0] ?? '' }])

  // Per-row validation, mirroring the backend validator so feedback is instant
  // (the debounced server validate is still the authoritative propose gate).
  // Duplicate/ambiguous intents (case-insensitive) are flagged on every row
  // that shares an intent.
  const intentCounts = new Map<string, number>()
  for (const r of routes) {
    const k = r.intent.trim().toLowerCase()
    if (k) intentCounts.set(k, (intentCounts.get(k) ?? 0) + 1)
  }
  const rowIssue = (r: SkillRoute) => {
    const intentEmpty = r.intent.trim() === ''
    const intentDup = !intentEmpty && (intentCounts.get(r.intent.trim().toLowerCase()) ?? 0) > 1
    const wfEmpty = r.workflow === ''
    const wfDangling = r.workflow !== '' && workflowOptions.length > 0 && !workflowOptions.includes(r.workflow)
    return { intentEmpty, intentDup, wfEmpty, wfDangling, intentBad: intentEmpty, wfBad: wfEmpty || wfDangling }
  }

  return (
    <div>
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Routes → workflows</div>
      {routes.length === 0 && <p className="text-xs text-gray-500 mb-1.5">No routes yet — add one to link an intent to a workflow, or ask the copilot.</p>}
      <ul className="space-y-1.5">
        {routes.map((r, i) => {
          const s = rowIssue(r)
          const msgs: { text: string; tone: 'err' | 'warn' }[] = []
          if (s.intentEmpty) msgs.push({ text: 'intent is required', tone: 'err' })
          else if (s.intentDup) msgs.push({ text: 'duplicate intent — ambiguous routing', tone: 'warn' })
          if (s.wfEmpty) msgs.push({ text: 'pick a workflow', tone: 'err' })
          else if (s.wfDangling) msgs.push({ text: `no activity named “${r.workflow}” on this agent (dangling)`, tone: 'err' })
          const rowRing = s.intentBad || s.wfBad ? 'border-l-2 border-l-red-400 pl-1.5' : s.intentDup ? 'border-l-2 border-l-amber-400 pl-1.5' : 'pl-2'
          return (
            <li key={i} className={rowRing}>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={r.intent}
                  onChange={e => update(i, { intent: e.target.value })}
                  placeholder="intent — when this fires (e.g. refund a customer)"
                  className={`flex-1 min-w-0 text-xs border rounded px-2 py-1 ${s.intentEmpty ? 'border-red-400' : s.intentDup ? 'border-amber-400' : 'border-gray-300'}`}
                />
                <span className="text-gray-300 text-xs shrink-0">→</span>
                <select
                  value={r.workflow}
                  onChange={e => update(i, { workflow: e.target.value })}
                  className={`text-xs border rounded px-1 py-1 max-w-[11rem] shrink-0 ${s.wfBad ? 'border-red-400 text-red-700' : 'border-gray-300'}`}
                  title={s.wfDangling ? `"${r.workflow}" is not an activity of this agent (dangling route)` : undefined}
                >
                  {r.workflow === '' && <option value="">— pick a workflow —</option>}
                  {r.workflow !== '' && !workflowOptions.includes(r.workflow) && <option value={r.workflow}>{r.workflow} (unknown)</option>}
                  {workflowOptions.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => onOpenWorkflow(r.workflow)}
                  disabled={s.wfBad}
                  className="text-xs text-indigo-600 hover:text-indigo-800 px-1 shrink-0 disabled:opacity-30"
                  title={s.wfBad ? 'Pick a valid workflow first' : `Open ${r.workflow} to edit (your skill edits are preserved via the unsaved-changes prompt)`}
                >
                  ↗
                </button>
                <button type="button" onClick={() => remove(i)} className="text-xs text-gray-400 hover:text-red-600 px-1 shrink-0" title="Remove route">✕</button>
              </div>
              {msgs.length > 0 && (
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-[10px]">
                  {msgs.map((m, k) => (
                    <span key={k} className={m.tone === 'err' ? 'text-red-600' : 'text-amber-600'}>
                      {m.tone === 'err' ? '✗' : '⚠'} {m.text}
                    </span>
                  ))}
                </div>
              )}
            </li>
          )
        })}
      </ul>
      <button type="button" onClick={add} className="mt-1.5 text-xs px-2 py-1 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50">+ Add route</button>
      {workflowOptions.length === 0 && <p className="mt-1 text-[10px] text-gray-400">This agent has no activities yet — a route needs an existing workflow to point at.</p>}
    </div>
  )
}

function RoutesTable({ routes, onOpenWorkflow }: { routes: SkillRoute[]; onOpenWorkflow: (workflow: string) => void }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Routes → workflows</div>
      {(!routes || routes.length === 0) ? (
        <p className="text-xs text-gray-500">This skill declares no <span className="font-mono">routes:</span>. Click Edit to add routes (or ask the copilot) to link it to workflows.</p>
      ) : (
        <ul className="space-y-1">
          {routes.map((r, i) => (
            <li key={i} className="text-xs flex items-center gap-2">
              <span className="text-gray-700">{r.intent || <span className="text-red-600 italic">(no intent)</span>}</span>
              <span className="text-gray-300">→</span>
              {r.workflow ? (
                <button type="button" onClick={() => onOpenWorkflow(r.workflow)} className="font-mono text-indigo-700 hover:underline">{r.workflow}</button>
              ) : <span className="text-red-600 italic">(no workflow)</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// UnroutedFixits — orphan fix-it affordances: this agent's activities that have
// triggers but no route yet. One-click "+ Add route" prefills the intent from
// the activity's first trigger and targets that workflow.
function UnroutedFixits({ activities, onAdd }: { activities: ActivitySummary[]; onAdd: (workflow: string, intent: string) => void }) {
  return (
    <div className="rounded border border-amber-200 bg-amber-50/60 p-2">
      <div className="text-xs font-semibold text-amber-800 mb-0.5">⚠ Workflows with no route ({activities.length})</div>
      <p className="text-[11px] text-amber-700 mb-1.5">These activities have triggers but no route points at them — add a route so this skill governs them.</p>
      <ul className="space-y-1">
        {activities.map(a => (
          <li key={a.name} className="flex items-center gap-2 text-xs">
            <span className="font-mono text-gray-700 shrink-0">{a.name}</span>
            <span className="text-gray-400 truncate">{a.triggers && a.triggers.length > 0 ? `“${a.triggers[0]}”` : ''}</span>
            <button type="button" onClick={() => onAdd(a.name, a.triggers?.[0] ?? '')}
              className="ml-auto shrink-0 text-[11px] px-2 py-0.5 rounded border border-amber-300 text-amber-800 hover:bg-amber-100">+ Add route</button>
          </li>
        ))}
      </ul>
    </div>
  )
}

// DiffView — a line-level diff of the draft vs the saved skill (review before propose).
function DiffView({ diff }: { diff: DiffLine[] }) {
  return (
    <div className="text-[11px] font-mono max-h-80 overflow-auto p-2 bg-gray-50 whitespace-pre">
      {diff.map((l, i) => (
        <div key={i} className={l.type === 'add' ? 'bg-green-50 text-green-800' : l.type === 'del' ? 'bg-red-50 text-red-800' : 'text-gray-400'}>
          {(l.type === 'add' ? '+ ' : l.type === 'del' ? '- ' : '  ') + (l.text || ' ')}
        </div>
      ))}
    </div>
  )
}

function ValidationBlock({ validation }: { validation: SkillValidation | null }) {
  if (!validation) return null
  return (
    <div className="space-y-1">
      {validation.ok ? (
        <p className="text-xs text-green-700">✓ valid</p>
      ) : (
        <ul className="text-xs text-red-700 list-disc list-inside">
          {validation.errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
      {validation.warnings && validation.warnings.length > 0 && (
        <ul className="text-xs text-amber-700 list-disc list-inside">
          {validation.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
        </ul>
      )}
    </div>
  )
}

// severityRank/severityStyle drive the Critique panel's sort + color coding —
// high first, then med, then low; anything unrecognized sorts last and reuses
// the "low" style rather than erroring.
const severityRank: Record<string, number> = { high: 0, med: 1, low: 2 }
const severityStyle: Record<string, string> = {
  high: 'border-red-200 bg-red-50',
  med: 'border-amber-200 bg-amber-50',
  low: 'border-gray-200 bg-gray-50',
}
const severityBadgeStyle: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  med: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
}

// CritiquePanel — ADR-121 D2: renders the "Critique" action's result. Purely
// advisory: every suggestion is dismissible client-side (never sent back to
// the server) and "Ask the copilot to apply this" only SEEDS the existing
// skill copilot chat — it never edits the skill itself.
function CritiquePanel({
  critique,
  dismissed,
  onDismiss,
  onClose,
  onAskCopilot,
  canAskCopilot,
}: {
  critique: SkillCritiqueResponse
  dismissed: Set<number>
  onDismiss: (index: number) => void
  onClose: () => void
  onAskCopilot: (s: SkillCritiqueSuggestion) => void
  canAskCopilot: boolean
}) {
  const visible = critique.suggestions
    .map((s, index) => ({ s, index }))
    .filter(({ index }) => !dismissed.has(index))
    .sort((a, b) => (severityRank[a.s.severity] ?? 3) - (severityRank[b.s.severity] ?? 3))

  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-purple-800">🔍 AI Critique</h3>
        <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:text-gray-800">✕ close</button>
      </div>
      {!critique.ok && (
        <p className="text-xs text-amber-700">
          The critique didn&apos;t come back cleanly{critique.error ? `: ${critique.error}` : '.'} Showing what the model returned below.
        </p>
      )}
      {critique.summary && <p className="text-xs text-gray-700">{critique.summary}</p>}
      {visible.length === 0 ? (
        <p className="text-xs text-gray-500">
          {critique.suggestions.length === 0
            ? critique.ok ? 'No suggestions — this skill looks solid.' : 'No suggestions returned.'
            : 'All suggestions dismissed.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {visible.map(({ s, index }) => (
            <li key={index} className={`rounded border px-2.5 py-1.5 ${severityStyle[s.severity] ?? severityStyle.low}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider">
                  <span className={`px-1.5 py-0.5 rounded-full ${severityBadgeStyle[s.severity] ?? severityBadgeStyle.low}`}>{s.severity}</span>
                  <span className="text-gray-500 normal-case font-normal">{s.area}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canAskCopilot && (
                    <button
                      type="button"
                      onClick={() => onAskCopilot(s)}
                      className="text-[11px] px-2 py-0.5 rounded border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50"
                      title="Seed the skill copilot chat with this suggestion — it edits, you still review + propose"
                    >
                      Ask the copilot to apply this
                    </button>
                  )}
                  <button type="button" onClick={() => onDismiss(index)} className="text-xs text-gray-400 hover:text-gray-700" title="Dismiss this suggestion">✕</button>
                </div>
              </div>
              <p className="text-xs text-gray-800 mt-1">{s.finding}</p>
              <p className="text-xs text-gray-600 mt-0.5"><span className="font-medium">Suggestion:</span> {s.suggestion}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
